import { useMemo } from 'react';
import { useAppStore } from '../lib/store';
import { TEMPLATES } from '../lib/templates';
import { ICONS } from './Icon';
import { useToast } from './Toast';

// Auth handled server-side by nginx HTTP Basic Auth
// Set ADMIN_USER and ADMIN_PASS in Coolify environment variables

interface Pack {
  id: string;
  name: string;
  tag: string;
  tagCls: string;
  desc: string;
  tpls: Array<{
    id: string;
    name: string;
    cat: string;
    sources: number;
    enabled: boolean;
    vert?: boolean;
  }>;
}

const PACKS: Pack[] = [
  {
    id: 'launch',
    name: 'Launch Set',
    tag: 'LIVE',
    tagCls: 'tag-live',
    desc: 'These 25 templates are the core launch set.',
    tpls: [
      { id: 'fs-clean', name: 'Full Screen — Clean', cat: 'Fullscreen', sources: 1, enabled: true },
      { id: 'fs-lbr', name: 'Full Screen + Logo (BR)', cat: 'Fullscreen', sources: 2, enabled: true },
      { id: 'fs-lt3', name: 'Full Screen + Lower Third', cat: 'Fullscreen', sources: 2, enabled: true },
      { id: 'fs-tick', name: 'Full Screen + Ticker Bar', cat: 'Fullscreen', sources: 2, enabled: true },
      { id: 'pip-lr', name: 'PiP — Camera Lower Right', cat: 'Picture-in-Picture', sources: 2, enabled: true },
      { id: 'pip-ll', name: 'PiP — Camera Lower Left', cat: 'Picture-in-Picture', sources: 2, enabled: true },
      { id: 'pip-lg', name: 'PiP — Large Camera (30%)', cat: 'Picture-in-Picture', sources: 2, enabled: true },
      { id: 'sbs-5050', name: '50/50 — Camera + Screen', cat: 'Side-by-Side', sources: 2, enabled: true },
      { id: 'sbs-cl', name: '1/3 Camera + 2/3 Screen', cat: 'Side-by-Side', sources: 2, enabled: true },
      { id: 'sbs-top', name: 'Screen Top + Camera Strip', cat: 'Side-by-Side', sources: 2, enabled: true },
      { id: 'int-5050', name: 'Two-Person Interview 50/50', cat: 'Interview', sources: 4, enabled: true },
      { id: 'int-host', name: 'Host Dominant + Guest Inset', cat: 'Interview', sources: 3, enabled: true },
      { id: 'int-sld', name: 'Speaker + Slides', cat: 'Interview', sources: 3, enabled: true },
      { id: 'int-pip', name: 'Full Slides + PiP Speaker', cat: 'Interview', sources: 2, enabled: true },
      { id: 'ch-lyr', name: 'Worship + Lyrics Overlay', cat: 'Church', sources: 3, enabled: true },
      { id: 'ch-pul', name: 'Sermon — Pulpit Full', cat: 'Church', sources: 3, enabled: true },
      { id: 'ch-scr', name: 'Sermon + Scripture Overlay', cat: 'Church', sources: 3, enabled: true },
      { id: 'ch-hold', name: 'Pre-Service Holding Screen', cat: 'Church', sources: 3, enabled: true },
      { id: 'tr-soon', name: 'Starting Soon', cat: 'Transition', sources: 3, enabled: true },
      { id: 'tr-brb', name: 'Be Right Back (BRB)', cat: 'Transition', sources: 2, enabled: true },
      { id: 'tr-out', name: 'Ending / Outro', cat: 'Transition', sources: 2, enabled: true },
      { id: 'tr-blk', name: 'Full Black — Cut to Black', cat: 'Transition', sources: 1, enabled: true },
      { id: 'st-chat', name: 'Camera + Live Chat Feed', cat: 'Streaming', sources: 2, enabled: true },
      { id: 'st-bub', name: 'Full Video + Camera Bubble', cat: 'Streaming', sources: 2, enabled: true },
      { id: 'st-triple', name: 'Game + Chat + Cam (Triple)', cat: 'Streaming', sources: 3, enabled: true },
    ]
  },
  {
    id: 'reserve',
    name: 'Reserve Templates',
    tag: 'RESERVE',
    tagCls: 'tag-month1',
    desc: 'Built but not yet shown. Enable to add to the live app.',
    tpls: [
      { id: 'fs-ltr', name: 'Full Screen + Logo (TR)', cat: 'Fullscreen', sources: 2, enabled: false },
      { id: 'fs-wm', name: 'Full Screen + Watermark', cat: 'Fullscreen', sources: 2, enabled: false },
      { id: 'pip-ul', name: 'PiP — Camera Upper Left', cat: 'Picture-in-Picture', sources: 2, enabled: false },
      { id: 'pip-ur', name: 'PiP — Camera Upper Right', cat: 'Picture-in-Picture', sources: 2, enabled: false },
      { id: 'sbs-cr', name: '2/3 Screen + 1/3 Camera', cat: 'Side-by-Side', sources: 2, enabled: false },
      { id: 'int-3p', name: 'Three-Person Panel', cat: 'Interview', sources: 6, enabled: false },
      { id: 'ch-2cam', name: 'Wide + Close Pulpit (PiP)', cat: 'Church', sources: 2, enabled: false },
      { id: 'tr-int', name: 'Intermission', cat: 'Transition', sources: 2, enabled: false },
      { id: 'tr-tech', name: 'Technical Difficulties', cat: 'Transition', sources: 1, enabled: false },
      { id: 'st-split', name: 'Reaction — Video + Cam', cat: 'Streaming', sources: 2, enabled: false },
      { id: 'st-alert', name: 'Camera + Alert Bar', cat: 'Streaming', sources: 2, enabled: false },
    ]
  },
  {
    id: 'vertical',
    name: 'Vertical / Shorts Pack',
    tag: 'MONTH 1',
    tagCls: 'tag-month1',
    desc: '9:16 portrait layouts for Reels, TikTok, YouTube Shorts.',
    tpls: [
      { id: 'v-facecam', name: 'Vertical — Solo Facecam', cat: 'Vertical', sources: 2, enabled: false, vert: true },
      { id: 'v-caption', name: 'Vertical — Facecam + Captions', cat: 'Vertical', sources: 3, enabled: false, vert: true },
      { id: 'v-split', name: 'Vertical — Cam Top + Screen', cat: 'Vertical', sources: 2, enabled: false, vert: true },
      { id: 'v-podcast', name: 'Vertical — Stacked 2-Person', cat: 'Vertical', sources: 2, enabled: false, vert: true },
      { id: 'v-pod-lt', name: 'Vertical — 2-Person + Names', cat: 'Vertical', sources: 4, enabled: false, vert: true },
      { id: 'v-l3', name: 'Vertical — Full Screen + L3', cat: 'Vertical', sources: 3, enabled: false, vert: true },
      { id: 'v-react', name: 'Vertical — Reaction (Clip+Cam)', cat: 'Vertical', sources: 2, enabled: false, vert: true },
      { id: 'v-soon', name: 'Vertical — Starting Soon', cat: 'Vertical', sources: 3, enabled: false, vert: true },
    ]
  },
  {
    id: 'podcast',
    name: 'Podcast Pack',
    tag: 'MONTH 2',
    tagCls: 'tag-month2',
    desc: 'Built for video podcasters recording or streaming with OBS.',
    tpls: [
      { id: 'pod-commentator', name: 'Podcast — Commentator BG', cat: 'Podcast', sources: 4, enabled: false },
      { id: 'pod-solo', name: 'Podcast — Solo Host', cat: 'Podcast', sources: 2, enabled: false },
      { id: 'pod-solo-lt', name: 'Podcast — Solo + Name Plate', cat: 'Podcast', sources: 2, enabled: false },
      { id: 'pod-2clean', name: 'Podcast — Two Hosts Clean', cat: 'Podcast', sources: 2, enabled: false },
      { id: 'pod-2lt', name: 'Podcast — Two Hosts + Names', cat: 'Podcast', sources: 4, enabled: false },
      { id: 'pod-host-guest', name: 'Podcast — Host Dominant', cat: 'Podcast', sources: 2, enabled: false },
      { id: 'pod-3clean', name: 'Podcast — Three Hosts', cat: 'Podcast', sources: 3, enabled: false },
      { id: 'pod-wide-close', name: 'Podcast — Wide + Close Up', cat: 'Podcast', sources: 2, enabled: false },
      { id: 'pod-notes', name: 'Podcast — Host + Talking Points', cat: 'Podcast', sources: 2, enabled: false },
    ]
  },
  {
    id: 'streamyard',
    name: 'StreamYard Multi-Guest Pack',
    tag: 'MONTH 3',
    tagCls: 'tag-month3',
    desc: 'Replicates all StreamYard default layouts.',
    tpls: [
      { id: 'sy-single', name: 'SY: Single — 1 Guest Full', cat: 'StreamYard', sources: 1, enabled: false },
      { id: 'sy-cropped', name: 'SY: Cropped — 1 Guest + Background', cat: 'StreamYard', sources: 2, enabled: false },
      { id: 'sy-group-2', name: 'SY: Group — 2 Guests (50/50)', cat: 'StreamYard', sources: 4, enabled: false },
      { id: 'sy-group-3', name: 'SY: Group — 3 Guests (Thirds)', cat: 'StreamYard', sources: 6, enabled: false },
      { id: 'sy-group-4', name: 'SY: Group — 4 Guests (2×2 Grid)', cat: 'StreamYard', sources: 4, enabled: false },
      { id: 'sy-group-5', name: 'SY: Group — 5 Guests', cat: 'StreamYard', sources: 5, enabled: false },
      { id: 'sy-group-6', name: 'SY: Group — 6 Guests (3×2 Grid)', cat: 'StreamYard', sources: 6, enabled: false },
      { id: 'sy-spotlight', name: 'SY: Spotlight — Right Rail', cat: 'StreamYard', sources: 5, enabled: false },
      { id: 'sy-spotlight-b', name: 'SY: Spotlight — Bottom Rail', cat: 'StreamYard', sources: 5, enabled: false },
      { id: 'sy-news-6040', name: 'SY: News — 60/40 Screen + Cam', cat: 'StreamYard', sources: 3, enabled: false },
      { id: 'sy-screen-rail', name: 'SY: Screen + Cam Rail Bottom', cat: 'StreamYard', sources: 4, enabled: false },
      { id: 'sy-pip', name: 'SY: Screen + PiP Camera', cat: 'StreamYard', sources: 2, enabled: false },
    ]
  },
  {
    id: 'creator',
    name: 'Creator / YouTube Pack',
    tag: 'MONTH 4',
    tagCls: 'tag-month4',
    desc: 'For YouTubers who stream or record with OBS.',
    tpls: [
      { id: 'cr-facecam', name: 'Creator — Facecam Only', cat: 'Creator', sources: 2, enabled: false },
      { id: 'cr-reaction', name: 'Creator — Reaction Split', cat: 'Creator', sources: 2, enabled: false },
      { id: 'cr-tutorial', name: 'Creator — Tutorial (Cam+Screen)', cat: 'Creator', sources: 2, enabled: false },
      { id: 'cr-unboxing', name: 'Creator — Unboxing / Product Shot', cat: 'Creator', sources: 2, enabled: false },
      { id: 'cr-dual-cam', name: 'Creator — Two-Angle Cut', cat: 'Creator', sources: 2, enabled: false },
      { id: 'cr-thumb', name: 'Creator — Thumbnail Reaction', cat: 'Creator', sources: 2, enabled: false },
      { id: 'cr-cinema', name: 'Creator — Cinema Screen (No Cam)', cat: 'Creator', sources: 1, enabled: false },
    ]
  },
  {
    id: 'broadcast',
    name: 'Broadcast / News Desk Pack',
    tag: 'MONTH 5',
    tagCls: 'tag-month5',
    desc: 'Professional broadcast layouts.',
    tpls: [
      { id: 'bc-anchor', name: 'News — Single Anchor', cat: 'Broadcast', sources: 3, enabled: false },
      { id: 'bc-split', name: 'News — Anchor + Remote', cat: 'Broadcast', sources: 4, enabled: false },
      { id: 'bc-three', name: 'News — Three Anchor Panel', cat: 'Broadcast', sources: 3, enabled: false },
      { id: 'bc-reporter', name: 'News — Reporter + B-Roll', cat: 'Broadcast', sources: 2, enabled: false },
      { id: 'bc-interview', name: 'News — In-Studio Interview', cat: 'Broadcast', sources: 3, enabled: false },
      { id: 'bc-score', name: 'Sports — Score Bug Layout', cat: 'Broadcast', sources: 3, enabled: false },
    ]
  },
  {
    id: 'webinar',
    name: 'Webinar / Talk Show Pack',
    tag: 'MONTH 6',
    tagCls: 'tag-month6',
    desc: 'For coaches, trainers, educators, and event hosts.',
    tpls: [
      { id: 'wb-presenter', name: 'Webinar — Presenter Full', cat: 'Webinar', sources: 2, enabled: false },
      { id: 'wb-slides', name: 'Webinar — Slides + Host', cat: 'Webinar', sources: 2, enabled: false },
      { id: 'wb-panel', name: 'Webinar — Panel (Host + 3)', cat: 'Webinar', sources: 5, enabled: false },
      { id: 'wb-qa', name: 'Webinar — Live Q&A View', cat: 'Webinar', sources: 3, enabled: false },
      { id: 'ts-talkshow', name: 'Talk Show — Host Bar Layout', cat: 'Webinar', sources: 5, enabled: false },
      { id: 'ts-couch', name: 'Talk Show — Couch Format', cat: 'Webinar', sources: 3, enabled: false },
    ]
  },
];

export function AdminPanel() {
  const { showToast, ToastComponent } = useToast();
  // No client-side auth — nginx Basic Auth protects /admin route

  const adminToggles = useAppStore((state) => state.adminToggles);
  const setAdminToggle = useAppStore((state) => state.setAdminToggle);

  const handleToggle = (templateId: string) => {
    setAdminToggle(templateId, !adminToggles[templateId]);
  };

  const handleEnableAll = () => {
    PACKS.forEach((pack) => {
      pack.tpls.forEach((tpl) => {
        setAdminToggle(tpl.id, true);
      });
    });
    showToast('All templates enabled');
  };

  const handleDisableAll = () => {
    PACKS.forEach((pack) => {
      pack.tpls.forEach((tpl) => {
        setAdminToggle(tpl.id, false);
      });
    });
    showToast('All templates disabled');
  };

  const handleSave = () => {
    const store = useAppStore.getState();
    store.saveAdminToggles();
    showToast('Changes saved — ScenR will update immediately');
  };

  const enabledCount = useMemo(() => {
    let count = 0;
    PACKS.forEach((pack) => {
      pack.tpls.forEach((tpl) => {
        if (adminToggles[tpl.id] !== false) count++;
      });
    });
    return count;
  }, [adminToggles]);

  const handleLogout = () => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", "/admin", true, "logout", "logout");
    xhr.setRequestHeader("Authorization", "Basic " + btoa("logout:logout"));
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        window.location.href = "/";
      }
    };
    xhr.send();
    setTimeout(() => {
      window.location.href = "/";
    }, 400);
  };

  return (
    <div className="admin-app" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header className="admin-header">
        <div className="header-logo">
          <div className="header-logo-icon">
            <svg viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5" width="14" height="14" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="16" height="12" rx="2" />
              <path d="M6 4v12M14 4v12M2 8h4M14 8h4M2 12h4M14 12h4" />
            </svg>
          </div>
          <span className="header-title">ScenR</span>
          <span className="header-badge">ADMIN</span>
        </div>
        <div className="header-actions">
          <button className="btn btn-ghost btn-sm" onClick={handleEnableAll}>Enable All</button>
          <button className="btn btn-red btn-sm" onClick={handleDisableAll}>Disable All</button>
          <button className="btn btn-teal btn-sm" onClick={handleSave}>
            {ICONS.copy}
            Save Changes
          </button>
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>

      <div className="stats-bar">
        <div className="stat">
          <span className="stat-val">{enabledCount}</span>
          <span className="stat-lbl">Enabled</span>
        </div>
        <div className="stat-div" />
        <div className="stat">
          <span className="stat-val">{TEMPLATES.length}</span>
          <span className="stat-lbl">Total Templates</span>
        </div>
        <div className="stat-div" />
        <div className="stat">
          <span className="stat-val">{PACKS.length}</span>
          <span className="stat-lbl">Packs</span>
        </div>
      </div>

      <div className="admin-main" style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {PACKS.map((pack) => (
          <div key={pack.id} className="pack-section" style={{ marginBottom: '28px' }}>
            <div className="pack-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid var(--bdr)' }}>
              <div className="pack-title-row" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="pack-name">{pack.name}</span>
                <span className="pack-count">{pack.tpls.length}</span>
                <span className={`pack-tag ${pack.tagCls}`}>{pack.tag}</span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button className="btn btn-green btn-sm" onClick={() => {
                  pack.tpls.forEach((t) => setAdminToggle(t.id, true));
                  showToast(`${pack.name}: all enabled`);
                }}>All On</button>
                <button className="btn btn-red btn-sm" onClick={() => {
                  pack.tpls.forEach((t) => setAdminToggle(t.id, false));
                  showToast(`${pack.name}: all disabled`);
                }}>All Off</button>
              </div>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--t2)', marginBottom: '12px' }}>{pack.desc}</p>
            <div className="tpl-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
              {pack.tpls.map((tpl) => {
                const isEnabled = adminToggles[tpl.id] !== false;
                return (
                  <div
                    key={tpl.id}
                    className={`tpl-row ${isEnabled ? 'enabled' : 'disabled'}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      borderRadius: 'var(--r-sm)',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--bdr)',
                      opacity: isEnabled ? 1 : 0.45
                    }}
                  >
                    <div style={{ width: '40px', height: '23px', borderRadius: '3px', background: 'var(--bg-input)', border: '1px solid var(--bdr)', overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ width: '100%', height: '100%', background: tpl.vert ? 'var(--pink-dim)' : 'var(--teal-dim)' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tpl.name}
                      </div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginTop: '1px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>{tpl.sources} sources</span>
                        {tpl.vert && <span style={{ fontSize: '8px', fontWeight: 700, background: 'var(--pink-dim)', color: 'var(--pink)', padding: '1px 4px', borderRadius: '3px' }}>9:16</span>}
                      </div>
                    </div>
                    <div
                      onClick={() => handleToggle(tpl.id)}
                      style={{
                        width: '36px',
                        height: '20px',
                        background: isEnabled ? 'var(--teal)' : 'var(--bg-input)',
                        borderRadius: '10px',
                        border: '1px solid var(--bdr-hi)',
                        position: 'relative',
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'background 200ms',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: isEnabled ? '18px' : '2px',
                          top: '2px',
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          background: 'white',
                          transition: 'left 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                          boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {ToastComponent}
    </div>
  );
}
